/**
 * Sound for the menus, from the outside: the screens only build DOM, so this listens to it.
 * Focus moving is a tick, a button is a confirm, Esc is a back; and the music follows the screen:
 * the title theme through the menus, the briefing's own track if there is one, the debrief and
 * memorial tracks (or silence) after a mission.
 */

import type { GameAudio } from '../audio';
import type { MusicDirector } from './musicDirector';

type Scene = 'menus' | 'briefing' | 'debrief' | 'memorial';

function sceneOf(screen: Element): Scene {
  const c = screen.classList;
  if (c.contains('menu-screen-memorial') || c.contains('menu-after-grave')) return 'memorial';
  if (c.contains('menu-screen-aftermath')) return 'debrief';
  if (c.contains('menu-screen-briefing')) return 'briefing';
  return 'menus';
}

export function wireMenuSounds(audio: GameAudio, music: MusicDirector): () => void {
  const backdrop = document.querySelector<HTMLElement>('.menu-backdrop');
  const stage = document.querySelector<HTMLElement>('.menu-stage');
  if (!backdrop || !stage) return () => {};

  const visible = (): boolean => !backdrop.classList.contains('menu-hidden');
  const onFocus = (): void => {
    if (visible()) audio.uiClick('move');
  };
  const onClick = (ev: MouseEvent): void => {
    const target = ev.target as Element | null;
    if (visible() && target?.closest('button')) audio.uiClick('confirm');
  };
  const onKey = (ev: KeyboardEvent): void => {
    if (visible() && ev.key === 'Escape') audio.uiClick('back');
  };
  backdrop.addEventListener('focusin', onFocus);
  backdrop.addEventListener('click', onClick);
  window.addEventListener('keydown', onKey);

  // Only a change of scene changes the music: moving between menu screens must not restart a theme.
  let scene: Scene | null = null;
  const follow = (): void => {
    const screen = stage.firstElementChild;
    if (!screen || !visible()) return;
    const next = sceneOf(screen);
    if (next === scene) return;
    scene = next;
    if (next === 'menus') music.title();
    else if (next === 'briefing') music.briefing();
    else music.afterMission(next === 'memorial');
  };
  const observer = new MutationObserver(follow);
  // The memorial beat is a class on the aftermath screen, not a new screen: watch classes too.
  observer.observe(stage, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  // The menus being hidden for a mission forgets the scene, so coming back always re-decides.
  const hidden = new MutationObserver(() => {
    if (!visible()) scene = null;
    else follow();
  });
  hidden.observe(backdrop, { attributes: true, attributeFilter: ['class'] });

  return () => {
    observer.disconnect();
    hidden.disconnect();
    backdrop.removeEventListener('focusin', onFocus);
    backdrop.removeEventListener('click', onClick);
    window.removeEventListener('keydown', onKey);
  };
}
